import { graphql } from "@/gql";
import {
  OrderByEnum,
  type GetUsersQuery,
  type UserFilterInput,
} from "@/gql/graphql";
import { useMutation, useQuery } from "@/utils/graphql";
import { formatTime } from "@/utils/time";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Pagination,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const GET_USERS = graphql(`
  query GetUsers(
    $limit: Int!
    $page: Int!
    $orderBy: UserOrderInput!
    $filters: UserFilterInput!
  ) {
    users(
      pagination: { page: { limit: $limit, page: $page } }
      orderBy: $orderBy
      filters: $filters
    ) {
      nodes {
        id
        name
        isValid
        createdOn
      }
      paginationInfo {
        total
      }
    }
  }
`);

export const CREATE_USER = graphql(`
  mutation CreateUser($input: CreateUserParamsInput!) {
    create_user(params: $input) {
      id
    }
  }
`);
export const UPDATE_USER = graphql(`
  mutation UpdateUser(
    $id: String!
    $valid: Boolean
    $name: String
    $password: String
  ) {
    update_user(
      params: { id: $id, is_valid: $valid, name: $name, password: $password }
    ) {
      id
    }
  }
`);
export const DELETE_USER = graphql(`
  mutation DeleteUser($input: DeleteUserParamsInput!) {
    delete_user(params: $input)
  }
`);

export default function UsersPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchName, setSearchName] = useState("");
  const [sortField, setSortField] = useState<"createdOn" | "name">("createdOn");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isEditing, setIsEditing] = useState(false);

  const orderByObj =
    sortField === "createdOn"
      ? { createdOn: sortOrder === "asc" ? OrderByEnum.Asc : OrderByEnum.Desc }
      : { name: sortOrder === "asc" ? OrderByEnum.Asc : OrderByEnum.Desc };

  const filters: UserFilterInput = {};
  if (searchName.trim()) {
    filters.name = { ilike: `%${searchName}%` };
  }

  const { data, isLoading, error, refetch } = useQuery(GET_USERS, {
    limit: pageSize,
    page,
    orderBy: orderByObj,
    filters,
  });
  const { mutate: createUser } = useMutation(CREATE_USER);
  const { mutate: deleteUser } = useMutation(DELETE_USER);
  const { mutate: updateUser } = useMutation(UPDATE_USER);
  const [form] = Form.useForm();
  const [showForm, setShowForm] = useState(false);

  const handleCreate = (values: {
    name: string;
    email: string;
    description?: string;
    institution_id: string;
  }) => {
    createUser(
      {
        input: {
          name: values.name,
        },
      },
      {
        onSuccess: () => {
          refetch();
          setShowForm(false);
          form.resetFields();
        },
      },
    );
  };

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        title={t("common.error", { message: error.message })}
      />
    );
  }

  const dataSource = (data?.users.nodes ?? []).map((node) => ({
    key: node.id,
    ...node,
  }));

  const total = data?.users?.paginationInfo?.total ?? 0;

  return (
    <Space orientation="vertical" size="large" className="w-full">
      <Space align="center" className="w-full justify-between">
        <Typography.Title level={3} className="m-0">
          {t("users.title")}
        </Typography.Title>
        <Button type="primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? t("common.cancel") : t("users.create")}
        </Button>
      </Space>

      {/* Filter Section */}
      <Space wrap>
        <Input.Search
          placeholder={t("common.search")}
          value={searchName}
          onChange={(e) => {
            setSearchName(e.target.value);
            setPage(0);
          }}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          value={sortField}
          onChange={(value) => {
            setSortField(value);
            setPage(0);
          }}
          style={{ width: 120 }}
          options={[
            { label: t("common.createdAt"), value: "createdOn" },
            { label: t("common.title"), value: "name" },
          ]}
        />
        <Select
          value={sortOrder}
          onChange={(value) => {
            setSortOrder(value);
            setPage(0);
          }}
          style={{ width: 100 }}
          options={[
            { label: t("common.ascending"), value: "asc" },
            { label: t("common.descending"), value: "desc" },
          ]}
        />
      </Space>

      {showForm && (
        <Card>
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item
              label={t("users.displayName")}
              name="name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("users.password")}
              name="password"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t("common.create")}
              </Button>
              <Button onClick={() => setShowForm(false)}>
                {t("common.cancel")}
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={dataSource}
        pagination={false}
        columns={[
          { title: t("common.id"), dataIndex: "id" },
          {
            title: t("users.displayName"),
            dataIndex: "name",
            render: (
              text: string,
              record: GetUsersQuery["users"]["nodes"][0],
            ) =>
              isEditing && record.id === form.getFieldValue("id") ? (
                <Input
                  defaultValue={text}
                  onBlur={(e) =>
                    updateUser(
                      { id: record.id as string, name: e.target.value },
                      {
                        onSuccess: () => {
                          (setIsEditing(false), refetch());
                        },
                      },
                    )
                  }
                  onPressEnter={(e) =>
                    updateUser(
                      {
                        id: record.id as string,
                        name: (e.target as HTMLInputElement).value,
                      },
                      {
                        onSuccess: () => {
                          (setIsEditing(false), refetch());
                        },
                      },
                    )
                  }
                />
              ) : (
                <span
                  onClick={() => {
                    form.setFieldsValue({ id: record.id, name: text });
                    setIsEditing(true);
                  }}
                >
                  {text}
                </span>
              ),
          },
          {
            title: t("common.status"),
            dataIndex: "isValid",
            render: (
              value: boolean,
              record: GetUsersQuery["users"]["nodes"][0],
            ) => (
              <Tag.CheckableTag
                checked={value}
                onChange={(checked) =>
                  updateUser(
                    { id: record.id as string, valid: checked },
                    { onSuccess: () => refetch() },
                  )
                }
              >
                {value ? t("users.active") : t("users.banned")}
              </Tag.CheckableTag>
            ),
          },
          {
            title: t("common.createdAt"),
            dataIndex: "createdOn",
            render: (value: string) => formatTime(value),
          },
          {
            title: t("common.actions"),
            key: "actions",
            render: (
              _: unknown,
              record: GetUsersQuery["users"]["nodes"][0],
            ) => (
              <Popconfirm
                title={t("users.confirmDelete")}
                onConfirm={() =>
                  deleteUser(
                    { input: { id: record.id as string } },
                    { onSuccess: () => refetch() },
                  )
                }
              >
                <Button size="small" danger>
                  {t("common.delete")}
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />

      {/* Pagination */}
      <Pagination
        current={page + 1}
        pageSize={pageSize}
        total={total}
        onChange={(current) => setPage(current - 1)}
        onShowSizeChange={(_, size) => {
          setPageSize(size);
          setPage(0);
        }}
        showSizeChanger
        showTotal={(total) => `${t("common.total")} ${total}`}
      />
    </Space>
  );
}
